import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { DailyBalanceRecord } from '../types';
import { formatNumber } from '../utils/format';
import { ApiService } from '../services/api';
import { format, subDays, subMonths, subYears } from 'date-fns';

interface BalanceChartProps {
  tokenSymbol: 'LIKE' | 'VUSD';
  address: string;
  isDark: boolean;
  decimals?: number;
  className?: string;
}

type TimeRange = '1d' | '7d' | '1m' | '6m' | '1y' | 'all';

interface ChartDataPoint {
  date: string;  // 日期字符串 YYYY-MM-DD
  timestamp: number;  // 用于显示的时间戳
  maxBalance: number;  // 当日最高余额
  minBalance: number;  // 当日最低余额
  endBalance: number;  // 当日结束余额
  formattedEndBalance: string;  // 格式化的结束余额
  formattedMaxBalance: string;  // 格式化的最高余额
  formattedMinBalance: string;  // 格式化的最低余额
  transactionCount: number;  // 当日交易数量
}

const BalanceChart: React.FC<BalanceChartProps> = ({
  tokenSymbol,
  address,
  isDark,
  decimals: propDecimals,
  className = ''
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('7d');
  const [dailyBalanceRecords, setDailyBalanceRecords] = useState<DailyBalanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const timeRangeConfig = {
    '1d': { label: '1D', days: 1 },
    '7d': { label: '7D', days: 7 },
    '1m': { label: '1M', days: 30 },
    '6m': { label: '6M', days: 180 },
    '1y': { label: '1Y', days: 365 },
    'all': { label: 'All', days: Infinity }
  };

  useEffect(() => {
    const fetchDailyBalance = async () => {
      try {
        setLoading(true);
        setError(null);

        // 首先获取统计信息
        const stats = await ApiService.getDailyBalanceStats(address, tokenSymbol);
        setTotalRecords(stats.total_records);

        if (stats.total_records === 0) {
          setDailyBalanceRecords([]);
          setLoading(false);
          return;
        }

        // 准备查询参数
        const options: any = {
          token: tokenSymbol,
          sort: 'asc'  // 按日期升序排列
        };

        // 根据选择的时间范围设置日期范围
        const today = new Date();
        let startDate: string | undefined;
        let endDate: string = format(today, 'yyyy-MM-dd');

        if (selectedTimeRange !== 'all') {
          const daysAgo = timeRangeConfig[selectedTimeRange].days;

          // 计算开始日期
          let startDateObj: Date;
          switch (selectedTimeRange) {
            case '1d':
              startDateObj = subDays(today, 1);
              break;
            case '7d':
              startDateObj = subDays(today, 7);
              break;
            case '1m':
              startDateObj = subMonths(today, 1);
              break;
            case '6m':
              startDateObj = subMonths(today, 6);
              break;
            case '1y':
              startDateObj = subYears(today, 1);
              break;
            default:
              startDateObj = subDays(today, daysAgo);
          }

          startDate = format(startDateObj, 'yyyy-MM-dd');
          options.start_date = startDate;
          options.end_date = endDate;
        }

        // 分页加载所有数据
        let allRecords: DailyBalanceRecord[] = [];
        let skip = 0;
        let hasMore = true;
        const limit = 1000;  // 每次请求1000条记录

        while (hasMore) {
          const response = await ApiService.getDailyBalance(address, {
            ...options,
            limit,
            skip
          });

          if (response.daily_records && response.daily_records.length > 0) {
            allRecords = [...allRecords, ...response.daily_records];
            skip += response.daily_records.length;

            // 检查是否还有更多数据
            if (response.daily_records.length < limit) {
              hasMore = false;
            }

            // 对于非all模式，限制获取的记录数
            if (selectedTimeRange !== 'all' && allRecords.length >= timeRangeConfig[selectedTimeRange].days) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        setDailyBalanceRecords(allRecords);
      } catch (err) {
        console.error('Failed to fetch daily balance:', err);
        setError('Failed to load daily balance data');
        setDailyBalanceRecords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDailyBalance();
  }, [address, tokenSymbol, selectedTimeRange]);

  // 将每日余额数据转换为图表数据
  const chartData = useMemo(() => {
    if (dailyBalanceRecords.length === 0) return [];

    const decimals = propDecimals || dailyBalanceRecords[0]?.decimals || 6;
    const dataPoints: ChartDataPoint[] = [];

    dailyBalanceRecords.forEach((record) => {
      // 将余额值转换为实际数值（除以decimals）
      const maxBalance = parseFloat(record.max_balance) / Math.pow(10, decimals);
      const minBalance = parseFloat(record.min_balance) / Math.pow(10, decimals);
      const endBalance = parseFloat(record.end_balance) / Math.pow(10, decimals);

      // 将日期转换为时间戳用于图表显示
      const timestamp = new Date(record.date).getTime();

      dataPoints.push({
        date: record.date,
        timestamp,
        maxBalance: Math.max(0, maxBalance),
        minBalance: Math.max(0, minBalance),
        endBalance: Math.max(0, endBalance),
        formattedEndBalance: formatNumber(Math.max(0, endBalance)),
        formattedMaxBalance: formatNumber(Math.max(0, maxBalance)),
        formattedMinBalance: formatNumber(Math.max(0, minBalance)),
        transactionCount: record.transaction_count
      });
    });

    // 根据时间范围进行数据采样（如果数据点过多）
    const maxPoints = {
      '1d': 24,     // 最多24个点（每小时一个）
      '7d': 7,      // 7天每天一个点
      '1m': 30,     // 30天每天一个点
      '6m': 180,    // 180天每天一个点
      '1y': 365,    // 365天每天一个点
      'all': 500    // 全部数据最多500个点
    };

    const targetPoints = maxPoints[selectedTimeRange] || 500;

    // 如果数据点超过目标数量，进行采样
    if (dataPoints.length > targetPoints) {
      const samplingInterval = Math.floor(dataPoints.length / targetPoints);
      const sampled: ChartDataPoint[] = [];

      // 保留第一个点
      sampled.push(dataPoints[0]);

      // 按间隔采样
      for (let i = samplingInterval; i < dataPoints.length - 1; i += samplingInterval) {
        sampled.push(dataPoints[i]);
      }

      // 保留最后一个点
      sampled.push(dataPoints[dataPoints.length - 1]);

      return sampled;
    }

    return dataPoints;
  }, [dailyBalanceRecords, selectedTimeRange, propDecimals]);

  // 自定义Tooltip显示
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div className={`${
          isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        } border rounded-lg p-3 shadow-lg min-w-[200px]`}>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-sm font-semibold mb-2`}>
            {new Date(data.timestamp).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </p>

          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Balance:</span>
              <span className={`${
                tokenSymbol === 'LIKE' ? 'text-blue-500' : 'text-purple-500'
              } font-semibold`}>
                {data.formattedEndBalance} {tokenSymbol}
              </span>
            </div>

            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>High:</span>
              <span className={`${isDark ? 'text-green-400' : 'text-green-600'} font-medium`}>
                {data.formattedMaxBalance} {tokenSymbol}
              </span>
            </div>

            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Low:</span>
              <span className={`${isDark ? 'text-red-400' : 'text-red-600'} font-medium`}>
                {data.formattedMinBalance} {tokenSymbol}
              </span>
            </div>

            {data.transactionCount > 0 && (
              <div className="flex justify-between pt-1 border-t border-gray-600/20">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Transactions:</span>
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  {data.transactionCount}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const chartColor = tokenSymbol === 'LIKE' ? '#3B82F6' : '#8B5CF6';

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {Object.entries(timeRangeConfig).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setSelectedTimeRange(key as TimeRange)}
              disabled={loading}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedTimeRange === key
                  ? tokenSymbol === 'LIKE'
                    ? 'bg-blue-500 text-white'
                    : 'bg-purple-500 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {config.label}
            </button>
          ))}
        </div>

        {!loading && totalRecords > 0 && (
          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {selectedTimeRange === 'all'
              ? `Total ${totalRecords.toLocaleString()} days`
              : `Showing ${chartData.length} days`}
          </div>
        )}
      </div>

      <div className="h-64">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-current mx-auto mb-2"></div>
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                Loading daily balance data...
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto mb-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-500">{error}</p>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                No {tokenSymbol} daily balance data available
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
            >
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return format(date, 'MMM dd');
                }}
                tick={{ fontSize: 11 }}
                axisLine={true}
                tickLine={false}
                stroke={isDark ? '#9CA3AF' : '#6B7280'}
              />
              <YAxis
                tickFormatter={(value) => formatNumber(value)}
                stroke={isDark ? '#9CA3AF' : '#6B7280'}
                fontSize={11}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* 显示每日结束余额的主线 */}
              <Line
                type="monotone"
                dataKey="endBalance"
                stroke={chartColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: chartColor, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BalanceChart;